import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Box, Checkbox, FormControlLabel, Link, Typography } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { updateUserTosConsent } from '@pec/aion-ui-core/actions/userConsent';
import { CommonRootState } from '@pec/aion-ui-core/combineReducers';
import { LanguageSwitcherComponent } from '@pec/aion-ui-i18next/components/LanguageSwitcher';
import { languageSwitcherParentTypes } from '@pec/aion-ui-i18next/constants';
import { VF1Logo } from './Nav/VF1Logo';
import { OpenInNewIcon } from './OpenInNewIcon';
import { LoadingButton } from './LoadingButton';
import { useScreenSize } from '../hooks/useScreenSize';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      fontFamily: "'Open Sans', sans-serif",
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      backgroundColor: '#07111e',
      padding: theme.spacing(2)
    },
    innerContainer: {
      display: 'flex',
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      [theme.breakpoints.down('sm')]: {
        justifyContent: 'flex-start'
      }
    },
    title: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: 36,
      fontWeight: 400,
      color: '#e3e2e6',
      letterSpacing: 0.1,
      textAlign: 'center',
      marginBottom: theme.spacing(1.25),
      [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(5),
        fontSize: 24,
        lineHeight: '32px'
      }
    },
    cardWrapper: {
      backgroundColor: '#fff',
      maxWidth: 531,
      padding: theme.spacing(4, 3, 3),
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 10
    },
    note: {
      fontFamily: "'Open Sans', sans-serif",
      marginTop: theme.spacing(1),
      paddingLeft: 34,
      color: '#43474e',
      fontSize: 12,
      lineHeight: '16px',
      letterSpacing: 0.4
    },
    link: {
      display: 'inline-flex',
      fontSize: 14,
      fontWeight: 600
    },
    icon: {
      marginTop: theme.spacing(0.25)
    },
    checkbox: {
      marginLeft: theme.spacing(0.25),
      marginRight: theme.spacing(0),
      alignItems: 'flex-start'
    },
    checkboxButton: {
      marginTop: -9,
      marginLeft: theme.spacing(-1.75),
      marginRight: theme.spacing(0.5)
    },
    checkboxLabel: {
      fontFamily: "'Open Sans', sans-serif",
      color: '#1a1c1e',
      fontSize: 14,
      lineHeight: '20px',
      letterSpacing: 0.25
    },
    button: {
      fontFamily: "'Open Sans', sans-serif",
      marginTop: theme.spacing(3),
      width: 111,
      borderRadius: 25,
      textTransform: 'initial',
      letterSpacing: 0.1,
      fontWeight: 600
    },
    footer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    },
    footerText: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: 12,
      lineHeight: '16px',
      letterSpacing: 0.75,
      color: '#e3e2e6'
    },
    languageText: {
      marginRight: theme.spacing(0.5),
      fontFamily: "'Open Sans', sans-serif",
      fontSize: 12,
      lineHeight: '16px',
      letterSpacing: 0.75,
      color: '#e3e2e6',
      paddingTop: theme.spacing(0.75)
    }
  })
);

type Props = {
  organizationId?: string;
};

export const TermsOfServicePage: React.FC<Props> = ({ organizationId }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isSmallDown } = useScreenSize();
  const { isFetchingTos: isFetching, isUpdating } = useSelector((state: CommonRootState) => state.userConsent);
  const { userInfo } = useSelector((state: CommonRootState) => state.userInfo);
  const [checked, setChecked] = React.useState<boolean>(false);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setChecked(checked);
  };

  const handleButtonClick = () => {
    dispatch(updateUserTosConsent(organizationId || userInfo?.primaryOrganizationId));
  };

  return (
    <Box className={classes.container}>
      <Box display="flex" justifyContent={isSmallDown ? 'center' : 'flex-start'}>
        <VF1Logo
          style={{
            height: isSmallDown ? 18 : 25,
            width: isSmallDown ? 148 : 196
          }}
        />
      </Box>
      <Box className={classes.innerContainer}>
        <Typography variant="h1" className={classes.title}>
          {t('components.termsOfService.title', 'Terms of Service')}
        </Typography>
        <Box className={classes.cardWrapper}>
          <FormControlLabel
            label={
              <Trans i18nKey="components.termsOfService.checkbox">
                I certify that I am authorized to accept these
                <Link href="https://veriforce.com/terms-of-service" target="_blank" className={classes.link}>
                  Terms of Service
                  <OpenInNewIcon className={classes.icon} />
                </Link>
                on behalf of the Customer using this Service.
              </Trans>
            }
            control={<Checkbox color="primary" classes={{ root: classes.checkboxButton }} />}
            classes={{
              label: classes.checkboxLabel,
              root: classes.checkbox
            }}
            checked={checked}
            onChange={handleCheckboxChange}
          />
          <Typography className={classes.note}>
            {t(
              'components.termsOfService.note',
              '[Note: If you are not authorized by the Customer to accept this agreement on their behalf, please request a correctly authorized and designated individual to do so.]'
            )}
          </Typography>
          <LoadingButton
            color="primary"
            variant="contained"
            isSubmitting={isFetching || isUpdating}
            isDisabled={!checked}
            className={classes.button}
            onClick={handleButtonClick}
          >
            {isUpdating ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('components.common.continueBtn', 'Continue')
            )}
          </LoadingButton>
        </Box>
      </Box>
      <Box className={classes.footer}>
        <Typography className={classes.footerText}>
          {t('components.footer.veriforceFooter', {
            defaultValue: '©{{year}} Veriforce. All Rights Reserved.',
            year: new Date().getFullYear()
          })}
        </Typography>
        <Box display="flex">
          <Typography className={classes.languageText}>
            {t('components.termsOfService.language', 'Language:')}
          </Typography>
          <LanguageSwitcherComponent parent={languageSwitcherParentTypes.registration} iconVisible={false} />
        </Box>
      </Box>
    </Box>
  );
};
